// frontend/src/pages/AskUccPage.js
//
// UCC Assistant — chat interface over the free template engine + Haiku
// fallback (when enabled). Members type questions about matches, players,
// captains, stats, fees/fines. Answers come from /api/chatbot/ask.
//
// Suggestive autocomplete:
//   - Curated catalog of questions the FREE template engine reliably answers.
//   - Each entry uses {name} placeholder substituted with the current user's
//     first name, so suggestions read as personal questions out of the box.
//   - When the composer is empty, we show 6–8 popular suggestions (one per
//     category) so members can tap-to-ask without typing.
//   - When they start typing, we filter the catalog and show up to 6 matches
//     ABOVE the composer. Each match is tappable — picking one fills the
//     input and submits immediately.
//
// Why hardcoded?  This is a UX feature, not a data pipeline.  ~50 quality
// suggestions beats 200 awkward ones, and we want strong control over which
// shapes the engine handles cleanly.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Public pages live under /club and must read from the anonymous API mirror.
// Derived from the URL rather than passed down as a prop: these files have
// deeply nested components (drilldown sheets, modals, breakdown panels) that
// make their own requests, and threading a prop through every one of them is
// how you end up with `P is not defined` crashing a page on click.
function PUB() {
  return (typeof window !== 'undefined' && window.location.pathname.startsWith('/club'))
    ? '/public' : '';
}


// ─── Suggestion catalog ───────────────────────────────────
// EVERY entry below has been verified against the engine's dispatcher
// regexes AND its handlers. Don't add new ones without confirming both:
//   1. A "wants…" regex in chatbotEngine.js matches the phrasing
//   2. The matched handler returns a real string (not null) for typical data
// Otherwise members tap a chip and get "we don't have that yet" — the
// exact bug we just fixed.
//
// `t` is the displayed text; `{name}` is replaced with the user's first
// name at render time. `cat` is for the category label.
const SUGGESTIONS = [
  // ── Batting ───────────────────────────────────────────
  { cat: 'Batting',  t: "How many runs has {name} scored this year?" },
  { cat: 'Batting',  t: "{name}'s highest score" },
  { cat: 'Batting',  t: "{name}'s batting average in 2026" },
  { cat: 'Batting',  t: "{name}'s strike rate" },
  { cat: 'Batting',  t: "{name}'s centuries in 2026" },
  { cat: 'Batting',  t: "{name}'s fifties this year" },
  { cat: 'Batting',  t: "{name}'s innings batted" },
  { cat: 'Batting',  t: "{name}'s not outs" },
  { cat: 'Batting',  t: "Top run scorer in 2026" },
  { cat: 'Batting',  t: "Most centuries in 2026" },
  { cat: 'Batting',  t: "Most fifties in 2026" },

  // ── Bowling ───────────────────────────────────────────
  { cat: 'Bowling',  t: "How many wickets has {name} taken?" },
  { cat: 'Bowling',  t: "{name}'s bowling average" },
  { cat: 'Bowling',  t: "{name}'s bowling economy" },
  { cat: 'Bowling',  t: "{name}'s best bowling figures" },
  { cat: 'Bowling',  t: "{name}'s five wicket hauls" },
  { cat: 'Bowling',  t: "{name}'s four wicket hauls" },
  { cat: 'Bowling',  t: "{name}'s bowled wickets" },
  { cat: 'Bowling',  t: "{name}'s lbw wickets" },
  { cat: 'Bowling',  t: "{name}'s caught wickets" },
  { cat: 'Bowling',  t: "Top wicket taker in 2026" },

  // ── Matches & captaincy ────────────────────────────────
  { cat: 'Matches',  t: "How many matches has {name} played?" },
  { cat: 'Matches',  t: "{name}'s debut match" },
  { cat: 'Matches',  t: "{name}'s last match" },
  { cat: 'Matches',  t: "How many matches has {name} captained?" },
  { cat: 'Matches',  t: "{name}'s captaincy wins" },
  { cat: 'Matches',  t: "Best captain in 2026" },
  { cat: 'Matches',  t: "Who captained on 24 May 2026?" },
  { cat: 'Matches',  t: "Who won on 24 May 2026?" },

  // ── Awards / MoTM ──────────────────────────────────────
  { cat: 'Awards',   t: "{name}'s man of the match awards" },
  { cat: 'Awards',   t: "Most MoTM awards in 2026" },

  // ── Attendance ─────────────────────────────────────────
  { cat: 'Attendance', t: "{name}'s attendance" },
  { cat: 'Attendance', t: "{name}'s attendance summary" },
  { cat: 'Attendance', t: "{name}'s attendance history" },
  { cat: 'Attendance', t: "{name}'s yearwise attendance" },
  { cat: 'Attendance', t: "{name}'s attendance rank in 2026" },
  { cat: 'Attendance', t: "How many matches has {name} been absent?" },
  { cat: 'Attendance', t: "{name}'s exemptions" },

  // ── Fees & fines (only confirmed-working shapes) ──────
  { cat: 'Money',    t: "{name}'s fees paid this year" },
  { cat: 'Money',    t: "{name}'s last payment" },
  { cat: 'Money',    t: "{name}'s latest fine" },
  { cat: 'Money',    t: "{name}'s fine history" },

  // ── Points / ranking ──────────────────────────────────
  { cat: 'Points',   t: "{name}'s total points in 2026" },
  { cat: 'Points',   t: "{name}'s points rank in 2026" },

  // ── Partnerships ──────────────────────────────────────
  { cat: 'Partnerships', t: "{name}'s best partnership" },
  { cat: 'Partnerships', t: "Highest partnership in 2026" },
];

// Suggestions shown when the input is empty pre-conversation. One per
// category, hand-picked for likely first questions. Indices verified
// against the SUGGESTIONS array above.
// Index 38 is the Money example ("{name}'s fees paid this year"). It is removed
// for public visitors below, since the public endpoint refuses financial
// questions — offering it as a suggestion would just invite a refusal.
const MONEY_SUGGESTION_INDEX = 38;

const POPULAR_INDICES = [
  0,   // Batting:     "How many runs has {name} scored this year?"
  11,  // Bowling:     "How many wickets has {name} taken?"
  21,  // Matches:     "How many matches has {name} played?"
  29,  // Awards:      "{name}'s man of the match awards"
  31,  // Attendance:  "{name}'s attendance"
  38,  // Money:       "{name}'s fees paid this year"
  43,  // Points:      "{name}'s points rank in 2026"
  45,  // Partnerships:"Highest partnership in 2026"
];

// `firstName` is the signed-in member's name. Anonymous visitors have none, so
// the examples fall back to a neutral placeholder rather than naming a real
// member of the club.
function personalize(text, firstName) {
  return text.replace(/\{name\}/g, firstName || 'ABC');
}

function tokenize(s) {
  return s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

function filterSuggestions(query, firstName) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const qTokens = tokenize(q);
  if (qTokens.length === 0) return [];

  // Score: count how many of the user's tokens appear in the personalized text.
  // Bias toward suggestions where ALL tokens hit. Cap at 6 results.
  const scored = SUGGESTIONS.map((s, i) => {
    const text = personalize(s.t, firstName).toLowerCase();
    const hits = qTokens.filter(tk => text.includes(tk)).length;
    return { i, s, hits };
  }).filter(x => x.hits > 0);

  scored.sort((a, b) => b.hits - a.hits);
  return scored.slice(0, 6).map(x => ({ ...x.s, _personalized: personalize(x.s.t, firstName) }));
}

export default function AskUccPage({ publicMode = false }) {
  const { member } = useAuth();
  const firstName = member?.display_name?.split(' ')[0] || '';

  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: publicMode
        ? 'Ask me about UCC matches, players, captains, scores, records and partnerships. Replace ABC with any player\u2019s name.'
        : `Hi ${firstName || 'there'}! Ask me about UCC matches, players, captains, scores, attendance, fees or fines.`,
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [focused, setFocused] = useState(false);
  // Voice: speech-to-text (mic) + text-to-speech (read aloud)
  const isIOS = typeof navigator !== 'undefined' && (
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (/Mac/.test(navigator.platform || '') && navigator.maxTouchPoints > 1)
  );
  // iOS exposes webkitSpeechRecognition but it routes through Siri and fails
  // silently, so we don't offer the in-app mic there (keyboard mic is used).
  const SpeechRec = typeof window !== 'undefined' && !isIOS && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const ttsOk = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [listening, setListening] = useState(false);
  const [voiceLang, setVoiceLang] = useState('en-IN'); // en-IN | hi-IN | pa-IN
  const [readAloud, setReadAloud] = useState(false);
  const ttsPrimed = useRef(false);
  const recogRef = useRef(null);
  const scrollRef = useRef(null);
  const convoCtx = useRef(null); // {playerId, playerName, year, intent} from previous answer
  const inputRef = useRef(null);

  // Personalized popular list for the pre-conversation view.
  const popular = useMemo(
    () => POPULAR_INDICES
      .filter(i => !(publicMode && i === MONEY_SUGGESTION_INDEX))
      .map(i => ({
        ...SUGGESTIONS[i],
        _personalized: personalize(SUGGESTIONS[i].t, firstName),
      })),
    [firstName, publicMode]
  );

  // Live filter results while the user types.
  const liveSuggestions = useMemo(
    () => filterSuggestions(input, firstName),
    [input, firstName]
  );

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Preload TTS voices (iOS/Safari populate getVoices() asynchronously).
  useEffect(() => {
    if (!ttsOk) return;
    const load = () => { try { window.speechSynthesis.getVoices(); } catch {} };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { try { window.speechSynthesis.onvoiceschanged = null; } catch {} };
  }, [ttsOk]);

  async function send(text) {
    const q = (text != null ? text : input).trim();
    if (!q || sending) return;
    // iOS keeps speech alive only if started during a user gesture. Prime it now
    // (synchronously, inside the tap) so the answer can be spoken after the await.
    if (readAloud && ttsOk) primeTTS();
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setSending(true);
    try {
      const r = await api.post(publicMode ? '/public/ask' : '/chatbot/ask', { question: q, context: convoCtx.current || {} });
      const answer = r.data?.answer || "Sorry, I couldn't find an answer.";
      const source = r.data?.source;
      if (r.data?.context) convoCtx.current = r.data.context;
      setMessages((m) => [...m, { role: 'bot', text: answer, source }]);
      if (readAloud) speak(answer);
    } catch (e) {
      const msg = e?.response?.data?.answer || e?.response?.data?.error || 'Something went wrong. Please try again.';
      setMessages((m) => [...m, { role: 'bot', text: msg, source: 'error' }]);
    } finally {
      setSending(false);
    }
  }

  // Unlock iOS speech: must be called inside a user gesture (tap). Speaking a
  // tiny silent utterance "wakes up" the synthesizer for the rest of the session.
  function primeTTS() {
    if (!ttsOk) return;
    try {
      window.speechSynthesis.resume();
      if (!ttsPrimed.current) {
        const u = new SpeechSynthesisUtterance('\u200b'); // zero-width, effectively silent
        u.volume = 0;
        window.speechSynthesis.speak(u);
        ttsPrimed.current = true;
      }
    } catch { /* ignore */ }
  }

  // Make answer text sound natural when spoken (e.g. cricket "5/9" → "5 for 9").
  function ttsClean(text) {
    return text
      .replace(/•/g, '')                                  // drop bullet glyphs
      .replace(/\(s\)/g, 's')                             // wicket(s) -> wickets
      .replace(/\b(\d{1,2})\s*\/\s*(\d{1,3})\b/g, '$1 for $2') // bowling figures W/R
      .replace(/\bSR\b/g, 'strike rate')
      .replace(/\bvs\.?\b/gi, 'versus')
      .replace(/\bLBW\b/g, 'L B W')
      .replace(/[^\S\n]{2,}/g, ' ')   // collapse spaces/tabs but keep line breaks
      .trim();
  }

  // Choose the most natural-sounding installed voice for the chosen language.
  // Prefers Enhanced/Premium/Natural/Neural voices and known pleasant names.
  function pickVoice(voices, lang) {
    const base = lang.split('-')[0];
    const inLang = voices.filter(v => v.lang && (v.lang === lang || v.lang.startsWith(base)));
    const pool = inLang.length ? inLang : voices;
    const nicePat = /(enhanced|premium|natural|neural|siri)/i;
    const niceNames = /(rishi|veena|lekha|samantha|ava|allison|nicky|serena|stephanie|kate|daniel|google uk english female|google us english|aaron|zoe)/i;
    return (
      pool.find(v => nicePat.test(v.name)) ||
      pool.find(v => niceNames.test(v.name)) ||
      pool.find(v => /female/i.test(v.name)) ||
      pool.find(v => v.lang === lang) ||
      pool[0] || null
    );
  }

  // Read text aloud — split into sentences/lines so the voice pauses naturally.
  function speak(text) {
    if (!ttsOk || !text) return;
    try {
      const synth = window.speechSynthesis;
      synth.cancel();
      synth.resume(); // iOS sometimes leaves it paused
      const voice = pickVoice(synth.getVoices() || [], voiceLang);
      // Break on line breaks and sentence ends for grammatical pauses.
      const chunks = ttsClean(text)
        .split(/\n+|(?<=[.?!])\s+/)
        .map(s => s.trim())
        .filter(Boolean);
      const queue = (i) => {
        if (i >= chunks.length) return;
        const u = new SpeechSynthesisUtterance(chunks[i]);
        u.lang = voiceLang;
        if (voice) u.voice = voice;
        u.rate = 0.95;   // slightly slower = clearer, less robotic
        u.pitch = 1.0;
        u.volume = 1;
        u.onend = () => queue(i + 1);
        try { synth.speak(u); } catch {}
      };
      setTimeout(() => queue(0), 60); // small delay helps iOS start reliably
    } catch { /* ignore */ }
  }

  // Push-to-talk: start/stop browser speech recognition, then auto-send.
  function toggleMic() {
    if (!SpeechRec) return;
    if (listening && recogRef.current) { recogRef.current.stop(); return; }
    const rec = new SpeechRec();
    rec.lang = voiceLang;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.continuous = false;
    recogRef.current = rec;
    let finalText = '';
    rec.onresult = (ev) => {
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t; else interim += t;
      }
      setInput((finalText + interim).trim());
    };
    rec.onerror = () => { setListening(false); };
    rec.onend = () => {
      setListening(false);
      const q = finalText.trim();
      if (q) send(q);
    };
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  }

  // Tap a suggestion -> clear input, blur, submit immediately.
  function pickSuggestion(text) {
    setInput('');
    setFocused(false);
    send(text);
  }

  const showLiveDropdown = focused && input.trim().length > 0 && liveSuggestions.length > 0;
  const showPreMessageList = messages.filter(m => m.role === 'user').length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', maxWidth: 720, margin: '0 auto' }}>
      {/* Header — back-to-fresh chip appears only after a question has been asked.
          Tapping it resets the conversation (clears messages, restores suggestion chips)
          so the user can start over without leaving the page. */}
      <div style={{ padding: '14px 16px 8px' }}>
        {!showPreMessageList && (
          <button
            onClick={() => {
              convoCtx.current = null;
              setMessages([
                {
                  role: 'bot',
                  text: publicMode
        ? 'Ask me about UCC matches, players, captains, scores, records and partnerships. Replace ABC with any player\u2019s name.'
        : `Hi ${firstName || 'there'}! Ask me about UCC matches, players, captains, scores, attendance, fees or fines.`,
                },
              ]);
              setInput('');
              setFocused(false);
            }}
            aria-label="Start over"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#1E3A6E', fontSize: '0.85rem', fontWeight: 700,
              padding: '4px 0', marginBottom: 6,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontFamily: 'Rajdhani,sans-serif',
            }}
          >
            ← Back
          </button>
        )}
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.4rem', color: '#1E3A6E' }}>
          ✨ Ask UCC
        </div>
        <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
          Questions about UCC cricket data only.
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '82%',
              background: m.role === 'user' ? '#1E3A6E' : (m.source === 'rejected' || m.source === 'error' ? '#fef3c7' : 'white'),
              color: m.role === 'user' ? 'white' : '#1f2937',
              border: m.role === 'user' ? 'none' : '1px solid #e5e7eb',
              borderRadius: 14,
              borderBottomRightRadius: m.role === 'user' ? 4 : 14,
              borderBottomLeftRadius: m.role === 'bot' ? 4 : 14,
              padding: '10px 13px',
              fontSize: '0.9rem',
              lineHeight: 1.5,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              whiteSpace: 'pre-wrap',
            }}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: '10px 13px', fontSize: '0.9rem', color: '#9ca3af' }}>
              Thinking…
            </div>
          </div>
        )}

        {/* Popular suggestions — pre-conversation only */}
        {showPreMessageList && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Try asking
            </div>
            {popular.map((s) => (
              <button
                key={s.t}
                onClick={() => pickSuggestion(s._personalized)}
                style={{
                  textAlign: 'left', background: 'white', border: '1px solid #d1d5db',
                  borderRadius: 10, padding: '9px 12px', fontSize: '0.82rem',
                  color: '#1E3A6E', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                <span style={{
                  fontSize: '0.62rem', fontWeight: 800, color: '#9ca3af',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  minWidth: 70,
                }}>{s.cat}</span>
                <span style={{ flex: 1 }}>{s._personalized}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Live suggestion dropdown — appears above composer while typing */}
      {showLiveDropdown && (
        <div style={{
          background: 'white',
          borderTop: '1px solid #e5e7eb',
          borderBottom: '1px solid #e5e7eb',
          maxHeight: 250,
          overflowY: 'auto',
        }}>
          <div style={{
            fontSize: '0.65rem', fontWeight: 800, color: '#9ca3af',
            padding: '8px 16px 4px', letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            Suggestions
          </div>
          {liveSuggestions.map((s) => (
            <button
              key={s.t}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pickSuggestion(s._personalized)}
              style={{
                width: '100%', textAlign: 'left',
                background: 'transparent', border: 'none', borderTop: '1px solid #f3f4f6',
                padding: '10px 16px', fontSize: '0.85rem',
                color: '#1f2937', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <span style={{
                fontSize: '0.6rem', fontWeight: 800, color: '#9ca3af',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                minWidth: 70,
              }}>{s.cat}</span>
              <span style={{ flex: 1 }}>{s._personalized}</span>
              <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>↵</span>
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <div style={{ padding: '10px 16px', borderTop: showLiveDropdown ? 'none' : '1px solid #e5e7eb', background: 'white' }}>
        {(SpeechRec || ttsOk) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            {SpeechRec && (
              <select
                value={voiceLang}
                onChange={(e) => setVoiceLang(e.target.value)}
                title="Voice language"
                style={{ fontSize: '0.72rem', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 8, color: '#1E3A6E', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700 }}
              >
                <option value="en-IN">EN / Hinglish</option>
                <option value="hi-IN">हिंदी</option>
                <option value="pa-IN">ਪੰਜਾਬੀ</option>
              </select>
            )}
            {ttsOk && (
              <button
                onClick={() => {
                  const n = !readAloud;
                  setReadAloud(n);
                  if (n) { primeTTS(); speak('Read aloud on'); }
                  else window.speechSynthesis.cancel();
                }}
                title="Read answers aloud"
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', padding: '4px 9px', border: '1px solid', borderColor: readAloud ? '#1E3A6E' : '#d1d5db', background: readAloud ? '#1E3A6E' : 'white', color: readAloud ? 'white' : '#1E3A6E', borderRadius: 8, cursor: 'pointer', fontFamily: 'Rajdhani,sans-serif', fontWeight: 700 }}
              >
                {readAloud ? '🔊' : '🔈'} Read aloud
              </button>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
        {SpeechRec && (
          <button
            onClick={toggleMic}
            disabled={sending}
            title={listening ? 'Stop' : 'Speak'}
            aria-label="Voice input"
            style={{
              width: 42, flexShrink: 0, border: 'none', borderRadius: 10, cursor: 'pointer',
              background: listening ? '#dc2626' : '#eef1f7', color: listening ? 'white' : '#1E3A6E',
              fontSize: '1.1rem', animation: listening ? 'uccPulse 1s infinite' : 'none',
            }}
          >
            {listening ? '⏺' : '🎙'}
          </button>
        )}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder={listening ? 'Listening…' : 'Ask a question…'}
          disabled={sending}
          style={{ flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: '0.9rem', outline: 'none' }}
        />
        <button
          onClick={() => send()}
          disabled={sending || !input.trim()}
          style={{
            background: '#1E3A6E', color: 'white', border: 'none', borderRadius: 10,
            padding: '0 18px', fontWeight: 700, fontFamily: 'Rajdhani,sans-serif',
            fontSize: '0.9rem',
            cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
            opacity: sending || !input.trim() ? 0.6 : 1,
          }}
        >
          Send
        </button>
        </div>
        {isIOS && (
          <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 6, textAlign: 'center' }}>
            Tip: tap the 🎤 on your keyboard to speak your question.
          </div>
        )}
      </div>
      <style>{`@keyframes uccPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,.5);} 50% { box-shadow: 0 0 0 6px rgba(220,38,38,0);} }`}</style>
    </div>
  );
}
